/**
 * URL State Manager Utility
 * Handles encoding/decoding application state to/from URL query parameters for sharing.
 */

import type { AppState, Coordinates, TravelMode, MapLanguage } from '../types';
import { validateCoordinates, validateTravelTime } from './validation';

/**
 * Extended state interface that includes UI preferences like map style and language
 */
export interface ExtendedState extends AppState {
  mapStyle?: 'streets' | 'satellite';
  language?: MapLanguage;
}

/**
 * Encodes application state to query string.
 * @param state - Application state to encode (can include extended state)
 * @returns Query string with encoded parameters
 */
export function encodeState(state: AppState | ExtendedState): string {
  const params = new URLSearchParams();

  if (state.origin) {
    params.set('lat', state.origin.lat.toString());
    params.set('lng', state.origin.lng.toString());
  }

  params.set('maxTime', state.maxTime.toString());
  params.set('mode', state.travelMode);
  params.set('speed', state.animationSpeed.toString());

  if (state.animationState === 'playing' || state.animationState === 'completed') {
    params.set('autoPlay', 'true');
  }

  // Add extended state if present
  const extendedState = state as ExtendedState;
  if (extendedState.mapStyle) {
    params.set('mapStyle', extendedState.mapStyle);
  }
  if (extendedState.language) {
    params.set('language', extendedState.language);
  }

  return params.toString();
}

/**
 * Decodes query string to application state.
 * @param queryString - Query string to decode
 * @returns Extended state (with mapStyle and language) or null if invalid
 */
export function decodeState(queryString: string): ExtendedState | null {
  try {
    const params = new URLSearchParams(queryString);

    // Parse and validate origin coordinates
    let origin: Coordinates | null = null;
    const latParam = params.get('lat');
    const lngParam = params.get('lng');

    if (latParam !== null && lngParam !== null) {
      const lat = parseFloat(latParam);
      const lng = parseFloat(lngParam);

      if (validateCoordinates({ lat, lng })) {
        origin = { lat, lng };
      } else {
        return null; // Invalid coordinates
      }
    }

    // Parse and validate max time
    const maxTimeParam = params.get('maxTime');
    if (!maxTimeParam) {
      return null;
    }
    const maxTime = parseInt(maxTimeParam, 10);
    if (!validateTravelTime(maxTime)) {
      return null;
    }

    // Parse and validate travel mode
    const modeParam = params.get('mode');
    if (!modeParam) {
      return null;
    }
    const travelMode = modeParam as TravelMode;
    const validModes: TravelMode[] = ['driving', 'walking', 'cycling', 'transit'];
    if (!validModes.includes(travelMode)) {
      return null;
    }

    // Parse animation speed (default: 1)
    const speedParam = params.get('speed');
    const animationSpeed = speedParam
      ? parseFloat(speedParam)
      : 1;
    if (isNaN(animationSpeed) || animationSpeed <= 0) {
      return null;
    }

    // Parse autoPlay (default: false)
    const autoPlayParam = params.get('autoPlay');
    const autoPlay = autoPlayParam === 'true';

    // Parse map style (optional)
    const mapStyleParam = params.get('mapStyle');
    const mapStyle = mapStyleParam === 'satellite' ? 'satellite' : 'streets';

    // Parse language (optional, default: 'en')
    const languageParam = params.get('language');
    const validLanguages: MapLanguage[] = ['en', 'zh-Hans', 'zh-Hant', 'es', 'fr', 'de', 'ja', 'ko'];
    const language = (languageParam && validLanguages.includes(languageParam as MapLanguage))
      ? (languageParam as MapLanguage)
      : 'en';

    return {
      origin,
      maxTime,
      currentTime: 0,
      travelMode,
      animationState: autoPlay ? 'idle' : 'idle',
      animationSpeed,
      mapStyle,
      language,
    };
  } catch (error) {
    console.error('Failed to decode state from URL:', error);
    return null;
  }
}

/**
 * Updates browser URL without navigation.
 * @param state - Application state to encode in URL (can include extended state)
 */
export function updateURL(state: AppState | ExtendedState): void {
  const queryString = encodeState(state);
  const newURL = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
  
  // Update URL without triggering navigation
  window.history.replaceState(
    { ...window.history.state, url: newURL },
    '',
    newURL
  );
}

/**
 * Gets current URL query string.
 * @returns Query string from current URL
 */
export function getCurrentQueryString(): string {
  return window.location.search.substring(1);
}

/**
 * Creates a shareable URL from application state.
 * Uses dynamic origin to work with any deployment (localhost, Vercel, etc.)
 * @param state - Application state (can include extended state)
 * @returns Full shareable URL
 */
export function createShareableURL(state: AppState | ExtendedState): string {
  const queryString = encodeState(state);
  // Use window.location.origin for dynamic URL (works with Vercel, localhost, etc.)
  // Ensure we have a trailing slash if pathname is just '/'
  const path = window.location.pathname === '/' ? '' : window.location.pathname;
  return `${window.location.origin}${path}?${queryString}`;
}

